import { useRouter, useRouterState } from '@tanstack/react-router';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PersonIcon } from '@/components/common/person-icon';
import { GhostButton } from '@/components/common';
import { useAuth } from '@/features/auth';
import { cn } from '@/lib/utils/cn';

interface UserMenuProps {
  userName?: string;
  userMeta?: string;
  avatarUrl?: string | null;
  variant?: 'icon' | 'sidebar';
  className?: string;
  accentClassName?: string;
  accentBgClassName?: string;
  accentHoverBgClassName?: string;
}

// Helper to get initials from name (max 2 characters)
function getInitials(name: string): string {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function UserMenu({
  userName = 'Client User',
  userMeta = 'client@example.com',
  avatarUrl,
  variant = 'icon',
  className,
  accentClassName = 'text-primary',
  accentBgClassName = 'bg-primary/15',
  accentHoverBgClassName = 'hover:bg-primary/20',
}: UserMenuProps) {
  const router = useRouter();
  const routerState = useRouterState();
  const auth = useAuth();
  const currentPath = routerState.location.pathname;

  // Use avatarUrl from prop or auth.user
  const userAvatarUrl = avatarUrl ?? auth.user?.avatarUrl;
  const initials = getInitials(userName);

  const getPortalRoute = (page: string) => {
    if (currentPath.startsWith('/admin')) return `/admin/${page}`;
    if (currentPath.startsWith('/operations')) return `/operations/${page}`;
    return `/client/${page}`;
  };

  const handleLogout = async () => {
    await auth.logout();
    window.location.href = '/login';
  };

  // Avatar content - either image or initials
  const avatarContent = userAvatarUrl ? (
    <img
      src={userAvatarUrl}
      alt={userName}
      className="h-full w-full rounded-full object-cover"
    />
  ) : (
    <span className="text-sm font-bold">{initials}</span>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === 'sidebar' ? (
          <GhostButton
            className={cn('flex w-full items-center gap-3 rounded-xl bg-gray-50 p-2 text-left transition-colors hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700', className)}
          >
            <div className={cn('flex h-10 w-10 items-center justify-center rounded-full overflow-hidden', accentBgClassName, !userAvatarUrl && accentClassName)}>
              {avatarContent}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-gray-900 dark:text-white">{userName}</p>
              <p className="truncate text-[10px] text-gray-500 dark:text-gray-400">{userMeta}</p>
            </div>
            <span className="material-symbols-outlined text-sm text-gray-400">unfold_more</span>
          </GhostButton>
        ) : (
          <GhostButton
            className={cn('h-8 w-8 rounded-full overflow-hidden', accentBgClassName, !userAvatarUrl && accentClassName, 'transition-colors', accentHoverBgClassName, 'focus:ring-2 focus:ring-primary focus:ring-offset-2')}
          >
            {avatarContent}
          </GhostButton>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="min-w-0 overflow-hidden font-normal">
          <div className="flex min-w-0 flex-col space-y-1">
            <p className="truncate text-sm font-medium leading-snug" title={userName}>
              {userName}
            </p>
            <p className="truncate text-xs leading-snug text-gray-500 dark:text-gray-400" title={userMeta}>
              {userMeta}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() => router.navigate({ to: getPortalRoute('profile') })}
            className="cursor-pointer"
          >
            Profile
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => router.navigate({ to: getPortalRoute('settings') })}
            className="cursor-pointer"
          >
            Settings
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleLogout}
          className="text-red-600 focus:text-red-600 dark:text-red-400"
        >
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
